import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  customerControllerGetCustomerQueryOptions,
  policyControllerGetPolicyQueryOptions,
} from 'korint-sdk';
import { useKorintProductConfig } from 'korint-sdk/react';
import { customerDisplayName } from '@folio/utils/customerDisplayName';

export function useContextLabel(urlContext?: Record<string, string>) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const { productsConfig } = useKorintProductConfig();

  const customerId = urlContext?.customerId;
  const policyId = urlContext?.policyId;
  const branchId = urlContext?.branchId;
  const hasContext = urlContext != null && Object.keys(urlContext).length > 0;

  const { data: policy } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId!, branchId ? { branchId } : undefined),
    enabled: !!policyId && !dismissed,
  });

  const customerIds = useMemo(
    () => (policyId ? (policy?.customers ?? []) : customerId ? [customerId] : []),
    [policyId, policy?.customers, customerId],
  );

  const customerQueries = useQueries({
    queries: dismissed
      ? []
      : customerIds.map(id => ({
          ...customerControllerGetCustomerQueryOptions(id, branchId ? { branchId } : undefined),
        })),
  });

  const isLoading = (!!policyId && !policy) || customerQueries.some(q => q.isLoading && !q.data);

  const displayName = useMemo(() => {
    const customers = customerQueries.map(q => q.data).filter(Boolean);
    if (customers.length === 0) {
      return customerIds.length > 0 ? customerIds[0] : null;
    }

    const productId = policy?.productId;
    const fieldsConfig = productId
      ? productsConfig[productId]?.customerInformations?.fieldsConfig
      : undefined;

    const names = customers
      .map(customer => {
        if (!customer) return null;
        return customerDisplayName([customer], fieldsConfig) || customer.id;
      })
      .filter(Boolean);

    return names.length > 0 ? names.join(' / ') : null;
  }, [customerIds, policy?.productId, productsConfig, customerQueries.map]);

  const prefillString = useMemo(() => {
    if (dismissed || !hasContext || !urlContext) return '';
    const parts = Object.values(urlContext).join(' / ');
    return `${t('conversation.contextPrefill', { context: parts })}\n\n`;
  }, [dismissed, hasContext, urlContext, t]);

  return {
    displayName,
    isLoading,
    isDismissed: dismissed,
    dismiss: () => setDismissed(true),
    prefillString,
    hasContext,
  };
}
